import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { ExternalIcon } from '../internal/icons';
import { VisuallyHidden } from './VisuallyHidden';
import styles from './Link.module.css';

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Opens a new tab. Adds `rel="noopener noreferrer"` (user-supplied links are
   * data, never trusted — ARCHITECTURE §6) and announces the new tab.
   */
  external?: boolean;
  /** Sits in running prose: underlined at rest so it is not colour-only. */
  inline?: boolean;
  size?: 'sm' | 'md';
  /** On a black band or a solid fill: inverse focus ring and inverse colour. */
  onInverse?: boolean;
  children: ReactNode;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { external = false, inline = false, size = 'md', onInverse = false, className, children, ...rest },
  ref,
) {
  return (
    <a
      {...rest}
      ref={ref}
      target={external ? '_blank' : rest.target}
      rel={external ? 'noopener noreferrer' : rest.rel}
      data-focus-scope={onInverse ? 'inverse' : undefined}
      className={clsx(
        styles.link,
        styles[size],
        inline && styles.inline,
        onInverse && styles.inverse,
        className,
      )}
    >
      <span className={styles.text}>{children}</span>
      {external && (
        <>
          <ExternalIcon className={styles.glyph} />
          <VisuallyHidden>(opens in a new tab)</VisuallyHidden>
        </>
      )}
    </a>
  );
});
